import React, { useMemo, useRef } from 'react'
import { Flex, Typography } from 'antd'
import { Virtuoso, VirtuosoHandle, StateSnapshot } from 'react-virtuoso'
import { Tag } from '@/shared/ui/components/Tag'
import { Log } from '../Log'
import {
  LogsSection,
  TaskLogsStats,
  LogsTable,
  LogsLoading,
  Content,
} from './styled'
import { TASK_TABS, TaskLogFilter } from '@/pages/tasks/ui/lib/taskTypes'
import { TASK_STATUSES, TaskItem } from '@/entities/task/model/taskTypes'
import { transformApiLogsToTaskLogs } from '@/entities/task/lib/transformTask'
import {
  extractLogsSummary,
  filterTaskLogsByType,
  shouldUseClientFallback,
} from '@/pages/tasks/model/taskLogsQuery.lib'
import { useTaskLogsQuery } from '@/pages/tasks/model/useTaskLogsQuery'
import { Icon } from '@/shared/ui/Icon/Icon'
import { getStatusTag } from '@/pages/tasks/ui/lib/getStatusTag'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'

const { Title } = Typography

export interface TaskLogsProps {
  taskId: string
  taskNumber?: number
  taskStatus: TaskItem['status'] | ''
}

export const TaskLogs: React.FC<TaskLogsProps> = ({
  taskId,
  taskNumber,
  taskStatus,
}) => {
  const [activeFilter, setActiveFilter] = React.useState<TaskLogFilter>(
    TASK_TABS.ALL
  )
  const { isSm, isXs } = useBreakpoint()
  const isMobile = isXs || isSm

  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const scrollStateByFilter = useRef<
    Partial<Record<TaskLogFilter, StateSnapshot>>
  >({})

  const isStarted = taskStatus === TASK_STATUSES.STARTED

  // Теги показывают глобальный тотал, отдельный от списка запрос под
  // фильтром 'all', не зависящий от того, какой фильтр выбран сейчас.
  // Когда activeFilter === 'all', это тот же самый запрос (один network-hit,
  // react-query дедуплицирует по queryKey).
  const summaryQuery = useTaskLogsQuery(taskId, 'all', {
    enabled: true,
    isStarted,
  })
  const query = useTaskLogsQuery(taskId, activeFilter, {
    enabled: true,
    isStarted,
  })

  const pages = useMemo(() => query.data?.pages ?? [], [query.data])

  const serverLogs = useMemo(
    () => transformApiLogsToTaskLogs(pages.flatMap((page) => page.log)),
    [pages]
  )

  const summaryPages = useMemo(
    () => summaryQuery.data?.pages ?? [],
    [summaryQuery.data]
  )

  const allLoadedLogs = useMemo(
    () => transformApiLogsToTaskLogs(summaryPages.flatMap((page) => page.log)),
    [summaryPages]
  )

  const summary = useMemo(
    () => extractLogsSummary(summaryPages),
    [summaryPages]
  )

  // Пока сервер по конкретному фильтру ничего не отдал (ещё грузится, или
  // баг бэкенда: level-фильтр валидируется, но ничего не находит), показываем
  // то, что уже загружено под фильтром 'all', отфильтрованное на клиенте.
  // Как только сервер реально начнёт отдавать данные по фильтру, список
  // переключается на них.
  const useFallback = shouldUseClientFallback(activeFilter, serverLogs.length)
  const logs = useFallback
    ? filterTaskLogsByType(allLoadedLogs, activeFilter)
    : serverLogs

  const handleTagClick = (type: TaskLogFilter) => {
    virtuosoRef.current?.getState((state) => {
      scrollStateByFilter.current[activeFilter] = state
    })
    setActiveFilter(activeFilter === type ? TASK_TABS.ALL : type)
  }

  const handleEndReached = () => {
    if (useFallback) {
      if (summaryQuery.hasNextPage && !summaryQuery.isFetchingNextPage) {
        summaryQuery.fetchNextPage()
      }
      return
    }

    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }

  const isFetchingNextPage = useFallback
    ? summaryQuery.isFetchingNextPage
    : query.isFetchingNextPage

  return (
    <LogsSection vertical gap={24}>
      {!isMobile && taskNumber && <Title level={3}>Логи выполнения</Title>}

      {isMobile && taskNumber && (
        <Flex justify="space-between" align="center" gap={24}>
          <Typography.Title level={3}>Задача #{taskNumber}</Typography.Title>
          {taskStatus && getStatusTag(taskStatus)}
        </Flex>
      )}
      {!taskNumber && <Title level={3}>Логи выполнения</Title>}
      <Content vertical gap={16}>
        <TaskLogsStats gap="8px 16px" wrap>
          <Tag
            variant="accent"
            isClicked={activeFilter === 'info'}
            onClick={() => handleTagClick('info')}
          >
            Информация {summary?.info ?? 0}
          </Tag>
          <Tag
            variant="attention"
            isClicked={activeFilter === 'warning'}
            onClick={() => handleTagClick('warning')}
          >
            Предупреждение {summary?.warning ?? 0}
          </Tag>
          <Tag
            variant="danger"
            isClicked={activeFilter === 'error'}
            onClick={() => handleTagClick('error')}
          >
            Ошибка {summary?.error ?? 0}
          </Tag>
        </TaskLogsStats>

        <LogsTable>
          {summaryQuery.isLoading ? (
            <LogsLoading align="center" justify="center">
              <Icon name="spinner" size={16} spin />
              <span style={{ marginLeft: '8px' }}>Загрузка логов...</span>
            </LogsLoading>
          ) : (
            <Virtuoso
              key={activeFilter}
              ref={virtuosoRef}
              restoreStateFrom={scrollStateByFilter.current[activeFilter]}
              style={{ height: '100%' }}
              data={logs}
              itemContent={(_index, log) => <Log log={log} />}
              endReached={handleEndReached}
              components={{
                Footer: () =>
                  isFetchingNextPage ? (
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <Icon name="spinner" size={16} spin />
                      <span style={{ marginLeft: '8px' }}>
                        Загрузка логов...
                      </span>
                    </div>
                  ) : null,
              }}
            />
          )}
        </LogsTable>
      </Content>
    </LogsSection>
  )
}
